import { Check } from "lucide-react";
import type { MapGeometry } from "../shared/types";
import { venueMapPinAnchor } from "./venueMapPinAnchor";
import {
  mapStandHeight,
  projectMapPoint,
  type VenueMapCamera,
} from "./venueMapProjection";

export interface VenueMapPinGroup {
  id: string;
  x: number;
  y: number;
  label: string;
  /** Editor-only visibility preview; public pins stay fully opaque. */
  opacity?: number;
  stops: {
    id: string;
    number: number;
    title: string;
    hasPlayed: boolean;
    isNext: boolean;
  }[];
}

/** Upright pins retain their bottom anchor in the inset corner of their own booth. */
export function VenueMapPins({
  camera,
  geometry,
  groups,
  onNavigate,
}: {
  camera: VenueMapCamera;
  geometry: MapGeometry;
  groups: VenueMapPinGroup[];
  onNavigate: (id: string) => void;
}) {
  if (camera.stageWidth <= 0 || camera.stageHeight <= 0) return null;
  return (
    <>
      {groups.map((group) => {
        const stand = geometry.stands.find((stand) => stand.id === group.id);
        if (stand?.hidden) return null;
        const anchor = projectMapPoint(camera, {
          ...venueMapPinAnchor(group, geometry),
          z: stand ? mapStandHeight(stand) : 0,
        });
        const columns = Math.min(3, group.stops.length);
        return (
          <div
            key={group.id}
            className={`venue-map-pin-group${group.stops.length > 1 ? " is-shared" : ""}`}
            style={{
              opacity: group.opacity,
              left: anchor.x,
              top: anchor.y,
              width:
                columns * 44 +
                (columns - 1) * 3 +
                (group.stops.length > 1 ? 6 : 0),
            }}
            role="group"
            aria-label={group.label}
            data-map-location={group.id}
          >
            {group.stops.map((stop) => (
              <button
                key={stop.id}
                type="button"
                className={`venue-map-pin${stop.hasPlayed ? " is-played" : ""}${stop.isNext ? " is-next" : ""}`}
                aria-label={`${stop.number}. ${stop.title}${stop.hasPlayed ? " · Completed" : ""}`}
                aria-current={stop.isNext ? "step" : undefined}
                title={stop.title}
                onClick={(event) => {
                  if (event.defaultPrevented) return;
                  event.stopPropagation();
                  onNavigate(stop.id);
                }}
              >
                <span className="venue-map-pin-number">{stop.number}</span>
                {stop.hasPlayed && (
                  <span className="venue-map-played-badge" aria-hidden="true">
                    <Check size={12} strokeWidth={3} />
                  </span>
                )}
              </button>
            ))}
          </div>
        );
      })}
    </>
  );
}
